import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { forwardRef, memo, useState } from 'react';

import Text from 'shopper/components/Text';

import TopContributorsRanking from 'components/TopContributorsRanking';
import TopContributorsRankingUser from 'components/TopContributorsRanking/TopContributorsRankingUser';

import { toFormattedCommentCount } from 'lib/comments';
import placeholder from 'lib/placeholder';
import { topContributorRankingQueryKeys } from 'lib/queryKeys';

import {
  getArticleCommentsTopContributorsList,
  getCommentTopContributorsList,
  getOfferCommentsTopContributorsList,
  getThreadCommentsTopContributorsList,
} from 'services/comment';

const ENDPOINTS = {
  offers: getOfferCommentsTopContributorsList,
  articles: getArticleCommentsTopContributorsList,
  threads: getThreadCommentsTopContributorsList,
  any: getCommentTopContributorsList,
};
const INITIAL_DAYS_RANGE = 7;
const TOP_CONTRIBUTORS_LIMIT = 5;

const TopContributorsCommentsRanking = forwardRef(
  ({ commentsSourceName, gaEventCategory = null, ...rest }, ref) => {
    const [daysRange, setDaysRange] = useState(INITIAL_DAYS_RANGE);

    const { data, isSuccess, isLoading, isFetching } = useQuery({
      queryKey: topContributorRankingQueryKeys.byComments({
        daysRange,
        commentsSourceName,
      }),
      queryFn: ({ signal }) =>
        ENDPOINTS[commentsSourceName](
          {
            days: daysRange,
            limit: TOP_CONTRIBUTORS_LIMIT,
          },
          { signal }
        ),
      select: ({ topContributors } = { topContributors: [] }) =>
        topContributors,
      cacheTime: 0.5 * 60 * 1000,
    });

    return (
      <TopContributorsRanking
        ref={ref}
        daysRange={daysRange}
        gaEventCategory={gaEventCategory}
        isLoading={isLoading || isFetching}
        title={placeholder('titles.topCommentsContributors')}
        onChangeDaysRange={setDaysRange}
        {...rest}
      >
        {isSuccess && data.length > 0 ? (
          data.map((contributor, index) => (
            <TopContributorsRankingUser
              key={contributor.userId}
              count={toFormattedCommentCount(contributor.commentCount)}
              gaEventCategory={gaEventCategory}
              userPlacing={index + 1}
              {...contributor}
            />
          ))
        ) : (
          <Text className="py-4 text-center" size="size2">
            {placeholder('empty.comments')}
          </Text>
        )}
      </TopContributorsRanking>
    );
  }
);

TopContributorsCommentsRanking.displayName = 'TopContributorsCommentsRanking';

TopContributorsCommentsRanking.propTypes = {
  commentsSourceName: PropTypes.string.isRequired,
  gaEventCategory: PropTypes.string,
};

export default memo(TopContributorsCommentsRanking);
